import { useState, useRef, useEffect, useCallback } from 'react';
import { MenuVariant, MenuSize, type MenuSizeConfig } from './types';

// ============================================================================
// Size Configurations
// ============================================================================

export const sizeConfig: Record<MenuSize, MenuSizeConfig> = {
  [MenuSize.Small]: {
    itemHeight: 32,
    paddingX: 12,
    paddingY: 6,
    fontSize: 14,
    lineHeight: 20,
    iconSize: 16,
    gap: 8,
    borderRadius: 4,
  },
  [MenuSize.Medium]: {
    itemHeight: 40,
    paddingX: 16,
    paddingY: 8,
    fontSize: 14,
    lineHeight: 20,
    iconSize: 20,
    gap: 8,
    borderRadius: 4,
  },
};

// ============================================================================
// Hook Props & Return Types
// ============================================================================

export interface UseMenuProps {
  variant?: MenuVariant | undefined;
  size?: MenuSize | undefined;
  expandOnClick?: boolean | undefined;
  onItemClick?: ((id: string) => void) | undefined;
}

export interface UseMenuReturn {
  config: MenuSizeConfig;
  isLight: boolean;
  menuRef: React.RefObject<HTMLDivElement | null>;
  openSubmenuId: string | null;
  containerBg: string;
  borderColor: string;
  handleItemClick: (id: string) => void;
  handleToggleSubmenu: (id: string) => void;
  handleCloseAllSubmenus: () => void;
}

// ============================================================================
// useMenu Hook
// ============================================================================

export const useMenu = ({
  variant = MenuVariant.Light,
  size = MenuSize.Medium,
  expandOnClick = true,
  onItemClick,
}: UseMenuProps): UseMenuReturn => {
  const config = sizeConfig[size];
  const isLight = variant === MenuVariant.Light;
  const menuRef = useRef<HTMLDivElement>(null);

  // Track which submenu is currently open
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);

  const handleItemClick = useCallback(
    (id: string) => {
      onItemClick?.(id);
    },
    [onItemClick]
  );

  const handleToggleSubmenu = useCallback((id: string) => {
    setOpenSubmenuId((current) => (current === id ? null : id));
  }, []);

  const handleCloseAllSubmenus = useCallback(() => {
    setOpenSubmenuId(null);
  }, []);

  // Close submenus when clicking outside
  useEffect(() => {
    if (!expandOnClick) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenSubmenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandOnClick]);

  // Container styles
  const containerBg = isLight ? '#ffffff' : '#2a4455';
  const borderColor = isLight ? '#e5e7eb' : 'rgba(255, 255, 255, 0.12)';

  return {
    config,
    isLight,
    menuRef,
    openSubmenuId,
    containerBg,
    borderColor,
    handleItemClick,
    handleToggleSubmenu,
    handleCloseAllSubmenus,
  };
};

// ============================================================================
// useMenuItem Hook
// ============================================================================

export interface UseMenuItemProps {
  id: string;
  disabled?: boolean | undefined;
  variant: MenuVariant;
  isSelected: boolean;
  isSubmenuOpen: boolean;
  expandOnClick: boolean;
  submenu?: unknown[] | undefined;
  onSelect: (id: string) => void;
  onToggleSubmenu: (id: string) => void;
}

export interface MenuItemStyles {
  bgColor: string;
  textColor: string;
  iconColor: string;
}

export interface UseMenuItemReturn {
  isHovered: boolean;
  showSubmenu: boolean;
  hasSubmenuItems: boolean;
  styles: MenuItemStyles;
  actualShowSubmenu: boolean;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleClick: (e: React.MouseEvent) => void;
}

export const useMenuItem = ({
  id,
  disabled = false,
  variant,
  isSelected,
  isSubmenuOpen,
  expandOnClick,
  submenu,
  onSelect,
  onToggleSubmenu,
}: UseMenuItemProps): UseMenuItemReturn => {
  const [isHovered, setIsHovered] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasSubmenuItems = submenu && submenu.length > 0;

  // Sync local showSubmenu with parent's isSubmenuOpen for click mode
  useEffect(() => {
    if (expandOnClick) {
      setShowSubmenu(isSubmenuOpen);
    }
  }, [isSubmenuOpen, expandOnClick]);

  // Handle submenu visibility on hover (only if not expandOnClick)
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (!expandOnClick && hasSubmenuItems) {
      submenuTimeoutRef.current = setTimeout(() => {
        setShowSubmenu(true);
      }, 150);
    }
  }, [expandOnClick, hasSubmenuItems]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (!expandOnClick) {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
      setShowSubmenu(false);
    }
  }, [expandOnClick]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      if (hasSubmenuItems) {
        e.stopPropagation();
        if (expandOnClick) {
          onToggleSubmenu(id);
        } else {
          setShowSubmenu(!showSubmenu);
        }
      } else {
        onSelect(id);
      }
    },
    [
      disabled,
      hasSubmenuItems,
      expandOnClick,
      id,
      showSubmenu,
      onToggleSubmenu,
      onSelect,
    ]
  );

  // Get styles based on variant and state
  const getStyles = useCallback((): MenuItemStyles => {
    const isLight = variant === MenuVariant.Light;
    const isExpanded = showSubmenu || isSubmenuOpen;

    let bgColor = 'transparent';
    let textColor = isLight ? '#1a1a1a' : '#ffffff';
    let iconColor = isLight ? '#6a7278' : 'rgba(255, 255, 255, 0.7)';

    if (disabled) {
      textColor = isLight ? '#b6bcc1' : 'rgba(255, 255, 255, 0.38)';
      iconColor = isLight ? '#b6bcc1' : 'rgba(255, 255, 255, 0.38)';
    } else if (isSelected || isExpanded) {
      bgColor = isLight ? '#e8eef3' : 'rgba(255, 255, 255, 0.16)';
      textColor = isLight ? '#2a4455' : '#ffffff';
      iconColor = isLight ? '#2a4455' : '#ffffff';
    } else if (isHovered) {
      bgColor = isLight ? '#f5f7f9' : 'rgba(255, 255, 255, 0.08)';
    }

    return { bgColor, textColor, iconColor };
  }, [variant, showSubmenu, isSubmenuOpen, disabled, isSelected, isHovered]);

  const styles = getStyles();
  const actualShowSubmenu = expandOnClick ? isSubmenuOpen : showSubmenu;

  return {
    isHovered,
    showSubmenu,
    hasSubmenuItems: !!hasSubmenuItems,
    styles,
    actualShowSubmenu,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  };
};
